import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PasswordInput from "@/components/PasswordInput";
import PasswordStrength, { scorePassword } from "@/components/PasswordStrength";
import AuthPromo from "@/components/AuthPromo";
import { toast } from "sonner";
import {
  User,
  Building2,
  Beaker,
  Banknote,
  ArrowRight,
  ArrowLeft,
  Mail,
  Loader2,
  Gift,
  X,
} from "lucide-react";

const AUTH_BG =
  "https://static.prod-images.emergentagent.com/jobs/06b5cb7e-c888-4c81-b88c-5244c1d0af32/images/fd1c566f5b8864f6832e4d35cf9140f3939f77c249aa2aa7f7d74ab738a59602.png";

const STEPS = ["Account", "Credentials", "Verify"];

export default function Register() {
  const [step, setStep] = useState(0);
  const [accountType, setAccountType] = useState("personal");
  const [bankingMode, setBankingMode] = useState("demo");
  const [name, setName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [policyAgreed, setPolicyAgreed] = useState(false);

  const [tempToken, setTempToken] = useState("");
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [promo, setPromo] = useState(null);
  const [promoCode, setPromoCode] = useState(
    (searchParams.get("promo") || "").toUpperCase()
  );

  useEffect(() => {
    if (!promoCode) {
      setPromo(null);
      return;
    }
    let cancelled = false;
    api
      .get(`/promo/${promoCode}`)
      .then(({ data }) => {
        if (cancelled) return;
        if (data.valid) {
          setPromo({
            code: data.code,
            headline: data.headline,
            description: data.description,
          });
          if (searchParams.get("promo") !== data.code) {
            setSearchParams({ promo: data.code }, { replace: true });
          }
        } else {
          setPromo(null);
          if (searchParams.get("promo")) setSearchParams({}, { replace: true });
          toast.error("That promo code isn't valid.");
        }
      })
      .catch(() => setPromo(null));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode]);

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const validateAccount = () => {
    if (!name || name.trim().length < 2) return "Please enter your full name.";
    if (accountType === "business" && !businessName.trim())
      return "Please enter your business name.";
    return null;
  };

  const validateCredentials = () => {
    if (!email || !/.+@.+\..+/.test(email)) return "Please enter a valid email.";
    const s = scorePassword(password);
    if (s.score < 2)
      return "Please choose a stronger password (use upper, lower, numbers, and symbols).";
    if (password !== confirm) return "Passwords do not match.";
    if (!policyAgreed) return "Please agree to the Privacy Policy to continue.";
    return null;
  };

  const submit = async () => {
    const err = validateCredentials();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/init", {
        email: email.trim(),
        password,
        name: name.trim(),
        account_type: accountType,
        banking_mode: bankingMode,
        business_name: accountType === "business" ? businessName.trim() : null,
        promo_code: promo?.code || null,
        policy_agreed: policyAgreed,
      });
      setTempToken(data.temp_token);
      setDevOtp(data.dev_otp || null);
      if (data.dev_otp)
        toast.warning(`Email send unavailable. Demo OTP: ${data.dev_otp}`);
      else toast.success("Code sent to your email.");
      next();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/resend-otp", {
        temp_token: tempToken,
      });
      setDevOtp(data.dev_otp || null);
      if (data.dev_otp) toast.warning(`Demo OTP: ${data.dev_otp}`);
      else toast.success("A new code has been sent.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length < 4) {
      toast.error("Enter the 6-digit code.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register/verify", {
        temp_token: tempToken,
        otp,
      });
      await refreshUser();
      if (data.promo_applied) {
        toast.success(
          `Welcome — promo ${data.promo_applied.code} credited $${data.promo_applied.amount.toFixed(
            2
          )}.`
        );
      } else {
        toast.success("Welcome to FinVault! Let's verify your identity.");
      }
      navigate("/app/verify-identity");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="bg-[#F0ECE0] flex flex-col">
        <div className="px-8 md:px-14 pt-8 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="register-logo">
            <Logo variant="wordmark" size={36} />
          </Link>
          <Link
            to="/login"
            className="text-sm text-[#4F4D45] hover:text-[#1B3526]"
            data-testid="register-link-login"
          >
            Have an account? Sign in
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 md:px-14 py-10">
          <div className="w-full max-w-md">
            {promo && (
              <div
                className="mb-6 rounded-xl border border-[#B58957]/30 bg-[#B58957]/10 px-4 py-3 flex items-start gap-3"
                data-testid="register-promo-banner"
              >
                <Gift className="w-4 h-4 mt-0.5 text-[#B58957]" />
                <div className="flex-1 text-sm text-[#0A0A08]">
                  <div className="font-semibold">{promo.headline}</div>
                  <div className="text-[#4F4D45]">{promo.description}</div>
                </div>
                <button
                  onClick={() => {
                    setPromo(null);
                    setPromoCode("");
                    setSearchParams({}, { replace: true });
                  }}
                  className="p-1 text-[#4F4D45] hover:text-[#0A0A08]"
                  data-testid="register-promo-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="text-eyebrow text-[#4F4D45]">Create your account</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
              Open your FinVault wallet
            </h1>
            <p className="text-[#4F4D45] mt-3 max-w-md">
              A calm, professional wallet built for 2026. Sign up in under a
              minute — you can verify your identity right after.
            </p>

            {/* Progress dots */}
            <div className="flex gap-2 mt-8">
              {STEPS.map((label, i) => (
                <div key={label} className="flex-1">
                  <motion.div
                    className={`h-1 rounded-full ${
                      i <= step ? "bg-[#1B3526]" : "bg-[#D2CDBE]"
                    }`}
                    initial={{ scaleX: 0.4 }}
                    animate={{ scaleX: i <= step ? 1 : 0.4 }}
                    transition={{ duration: 0.4 }}
                    style={{ transformOrigin: "left" }}
                  />
                  <motion.div
                    className={`mt-2 text-xs ${
                      i === step ? "text-[#0A0A08] font-medium" : "text-[#4F4D45]"
                    }`}
                    data-testid={`register-step-label-${i}`}
                  >
                    {label}
                  </motion.div>
                </div>
              ))}
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={`step-${step}`}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.25 }}
                className="mt-8"
              >
                {step === 0 && (
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-3">
                      <TypeCard
                        Icon={User}
                        label="Personal"
                        active={accountType === "personal"}
                        onClick={() => setAccountType("personal")}
                        testId="register-account-personal"
                      />
                      <TypeCard
                        Icon={Building2}
                        label="Business"
                        active={accountType === "business"}
                        onClick={() => setAccountType("business")}
                        testId="register-account-business"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <TypeCard
                        Icon={Beaker}
                        label="Demo"
                        subtitle="$1,000 sandbox"
                        active={bankingMode === "demo"}
                        onClick={() => setBankingMode("demo")}
                        testId="register-mode-demo"
                      />
                      <TypeCard
                        Icon={Banknote}
                        label="Live"
                        subtitle="Real transactions"
                        active={bankingMode === "live"}
                        onClick={() => setBankingMode("live")}
                        testId="register-mode-live"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#4F4D45]">Full name</Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Ada Lovelace"
                        className="mt-1"
                        data-testid="register-input-name"
                      />
                    </div>
                    {accountType === "business" && (
                      <div>
                        <Label className="text-xs text-[#4F4D45]">
                          Business name
                        </Label>
                        <Input
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          placeholder="Lovelace & Co"
                          className="mt-1"
                          data-testid="register-input-business-name"
                        />
                      </div>
                    )}
                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={() => {
                          const err = validateAccount();
                          if (err) toast.error(err);
                          else next();
                        }}
                        className="bg-[#1B3526] hover:bg-[#0F2517]"
                        data-testid="register-step0-next"
                      >
                        Continue <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}

                {step === 1 && (
                  <div className="space-y-5">
                    <div>
                      <Label className="text-xs text-[#4F4D45]">Email</Label>
                      <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="mt-1"
                        data-testid="register-input-email"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#4F4D45]">Password</Label>
                      <PasswordInput
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-1"
                        data-testid="register-input-password"
                      />
                      <PasswordStrength password={password} className="mt-2" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#4F4D45]">
                        Confirm password
                      </Label>
                      <PasswordInput
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        className="mt-1"
                        data-testid="register-input-confirm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#4F4D45]">
                        Promo code (optional)
                      </Label>
                      <Input
                        value={promoCode}
                        onChange={(e) =>
                          setPromoCode(e.target.value.toUpperCase().trim())
                        }
                        placeholder="e.g. LAUNCH25"
                        className="mt-1 uppercase"
                        data-testid="register-input-promo"
                      />
                    </div>
                    <label className="flex items-start gap-2 text-sm text-[#4F4D45]">
                      <Checkbox
                        checked={policyAgreed}
                        onCheckedChange={(v) => setPolicyAgreed(!!v)}
                        data-testid="register-policy-check"
                      />
                      <span>
                        I agree to the{" "}
                        <Link
                          to="/privacy"
                          className="text-[#1B3526] underline"
                          target="_blank"
                        >
                          Privacy Policy
                        </Link>{" "}
                        and{" "}
                        <Link
                          to="/terms"
                          className="text-[#1B3526] underline"
                          target="_blank"
                        >
                          Terms of Service
                        </Link>
                        .
                      </span>
                    </label>
                    <div className="flex justify-between pt-2">
                      <Button
                        variant="ghost"
                        onClick={back}
                        data-testid="register-step1-back"
                      >
                        <ArrowLeft className="mr-2 w-4 h-4" />
                        Back
                      </Button>
                      <Button
                        onClick={submit}
                        disabled={loading}
                        className="bg-[#1B3526] hover:bg-[#0F2517]"
                        data-testid="register-submit"
                      >
                        {loading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            Create account <ArrowRight className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <div className="flex items-center gap-2 text-[#1B3526]">
                      <Mail className="w-4 h-4" />
                      <span className="text-sm">
                        A 6-digit code was sent to <strong>{email}</strong>
                      </span>
                    </div>
                    <div>
                      <Label className="text-xs text-[#4F4D45]">
                        Enter verification code
                      </Label>
                      <Input
                        value={otp}
                        onChange={(e) =>
                          setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        inputMode="numeric"
                        placeholder="123456"
                        className="mt-1 font-mono tracking-[0.4em] text-center text-xl"
                        data-testid="register-input-otp"
                      />
                      {devOtp && (
                        <p className="mt-2 text-xs text-[#B58957]">
                          Dev OTP: <span className="font-mono">{devOtp}</span>
                        </p>
                      )}
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <button
                        onClick={resendOtp}
                        disabled={loading}
                        className="text-[#1B3526] hover:underline"
                        data-testid="register-resend-otp"
                      >
                        Resend code
                      </button>
                      <div className="text-[#4F4D45]">
                        Wrong email?{" "}
                        <button
                          onClick={back}
                          className="text-[#1B3526] hover:underline"
                          data-testid="register-otp-back"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                    <Button
                      onClick={verifyOtp}
                      disabled={loading}
                      className="w-full bg-[#1B3526] hover:bg-[#0F2517]"
                      data-testid="register-verify-otp"
                    >
                      {loading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>Verify & continue</>
                      )}
                    </Button>
                    <p className="text-xs text-[#4F4D45] text-center">
                      Right after this you'll finish identity verification —
                      just an ID photo and a quick selfie.
                    </p>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Right: promo image / marketing */}
      <div
        className="hidden lg:block relative bg-cover bg-center"
        style={{ backgroundImage: `url(${AUTH_BG})` }}
      >
        <div className="absolute inset-0 bg-[#0A130E]/70" />
        <div className="relative h-full flex items-end p-12">
          <AuthPromo />
        </div>
      </div>
    </div>
  );
}

function TypeCard({ Icon, label, subtitle, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-xl border p-4 transition-colors ${
        active
          ? "border-[#1B3526] bg-white"
          : "border-[#D2CDBE] bg-[#F0ECE0]/70 hover:bg-white"
      }`}
    >
      <Icon
        className={`w-5 h-5 ${active ? "text-[#1B3526]" : "text-[#4F4D45]"}`}
      />
      <div
        className={`mt-3 font-display font-semibold ${
          active ? "text-[#0A0A08]" : "text-[#4F4D45]"
        }`}
      >
        {label}
      </div>
      {subtitle && (
        <div className="text-xs text-[#4F4D45] mt-1">{subtitle}</div>
      )}
    </button>
  );
}
